import { SafeAreaView } from "@hg/layouts"
import cls from "classnames"
import { useCallback, useState } from "react"

import {
    getIsMute,
    setMusicMute,
    soundController,
    unsetMusicMute,
} from "@/assets/audios"
import { showConfirm } from "@/components/Confirm"
import { useLottery } from "@/components/Lottery"
import { i18n } from "@/i18n"
import { useLogin, useLogout, useRoleInfo } from "@/user/session"
import { adapter } from "@/utils/sdk"
import UserInfo from "./UserInfo"

import styles from "./index.module.scss"

const { returnButton } = adapter.ui
const logoutButton = !returnButton
const inBilibiliSource = adapter.source.from === "bilibili"

interface HeaderButtonProps {
    icon:
        | "hypergryph"
        | "bilibili"
        | "home"
        | "bgm"
        | "bgm-off"
        | "gift"
        | "exit"
    href?: string
    onClick?: () => void
    className?: string
}

const HeaderButton: React.FC<HeaderButtonProps> = (props) => {
    const { icon, href, onClick, className } = props

    if (href) {
        return (
            <a
                className={cls(styles.headerButton, className)}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                onClick={onClick}
                aria-label={icon}
            >
                <div className={styles.icon} data-type={icon} />
            </a>
        )
    }

    return (
        <button
            className={cls(styles.headerButton, className)}
            onClick={onClick}
            type="button"
            aria-label={icon}
        >
            <div className={styles.icon} data-type={icon} />
        </button>
    )
}

interface CommonButtonsProps {
    showGift?: boolean
    onClickGift?: () => void
}

const CommonButtons: React.FC<CommonButtonsProps> = (props) => {
    const { showGift, onClickGift } = props

    const [enableMusic, setEnableMusic] = useState(getIsMute())

    return (
        <>
            <HeaderButton
                icon="home"
                href="https://www.arknights.global/"
            />
            <HeaderButton
                icon={enableMusic ? "bgm" : "bgm-off"}
                onClick={() => {
                    if (enableMusic) {
                        setMusicMute()
                        setEnableMusic(false)
                        soundController.disable()
                    } else {
                        unsetMusicMute()
                        setEnableMusic(true)
                        soundController.enable()
                    }
                }}
            />
            {showGift && <HeaderButton icon="gift" onClick={onClickGift} />}
            {returnButton && (
                <HeaderButton
                    icon="exit"
                    onClick={() => adapter.ui.onReturn()}
                />
            )}
        </>
    )
}

export const Header = () => {
    const user = useRoleInfo()
    const login = useLogin()
    const logout = useLogout()

    const onLogout = useCallback(() => {
        showConfirm({
            content: i18n.get("common.logoutConfirm"),
            onOk: async () => {
                await logout()
                window.location.reload()
            },
        })
    }, [logout])

    const lottery = useLottery()
    lottery?.refreshLotteryStatus()

    return (
        <>
            <SafeAreaView mode="margin" edges={["right"]} className={styles.header}>
                {user ? (
                    <>
                        <UserInfo
                            channelId={user.channelId}
                            nickName={user.nickName}
                            hideLogout={!logoutButton}
                            onLogout={onLogout}
                        />
                        <CommonButtons
                            showGift
                            onClickGift={() => lottery?.start()}
                        />
                    </>
                ) : (
                    <>
                        <HeaderButton
                            icon="hypergryph"
                            onClick={() => login()}
                        />
                        <CommonButtons />
                    </>
                )}
            </SafeAreaView>
            {lottery?.modal}
        </>
    )
}
